import * as React from 'react';
import { Component } from 'react';
import { SketchPicker } from 'react-color';
import propz from 'propz';
import { History, Location } from 'history';
import { AppUser } from 'Src/views/App/App';
import { KitShirtPreview } from 'Src/components/KitLab/KitShirtPreview';
import { KitShortsPreview } from 'Src/components/KitLab/KitShortsPreview';
import { KitSkortPreview } from 'Src/components/KitLab/KitSkortPreview';
import {
  getKitLabKits,
  createKitLabKit,
  updateKitLabKit,
  setKitLabDefaultKit,
  deleteKitLabKit,
  KitLabBackendKit
} from 'Src/helpers/service/admin/kitLab';
import {
  SHIRT_PATTERNS,
  BOTTOM_PATTERNS,
  isSkortPatternId,
  mapBottomToSkortPattern,
  getDefaultGenderFromUser,
  getDefaultConfig,
  isSkortAllowedForGender,
  normalizeBottomPatternForGender,
  KitPreset
} from 'Src/helpers/kitLab/kitLab';
import { KitPatternVariant, ShortsSideTrianglesPosition } from 'Src/types/kitLab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck, faPen, faPlus, faStar, faTrash, faTshirt } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import { ListEmptyState } from 'Src/components/ListEmptyState/ListEmptyState';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { getName, searchFunctionSportsWithFavorite } from 'Src/helpers/autocomplete/autocomplete';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { getSchool } from 'Src/helpers/service/admin/user';
import {
  KitBottomPattern,
  Gender,
  DetailColorSource,
  SleevePattern,
  GirlsBottomType,
  ViewMode,
  EditorStep,
  ColorFieldKey,
  CollarStyle
} from 'Src/types/kitLab';
import './KitLab.scss';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface State {
  gender: Gender;
  shirtPattern: KitPatternVariant;
  bottomPattern: KitBottomPattern;

  primaryColor: string;
  secondaryColor: string;
  shortsPrimaryColor: string;
  shortsSecondaryColor: string;

  showPocket: boolean;
  showCollar: boolean;
  showSleeves: boolean;
  showShoulderTriangles: boolean;
  showSleeveCuffs: boolean;

  pocketColorSource: DetailColorSource;
  sleevesColorSource: DetailColorSource;
  collarColorSource: DetailColorSource;
  shoulderTrianglesColorSource: DetailColorSource;
  sleeveCuffsColorSource: DetailColorSource;

  pocketCustomColor: string;
  sleevesCustomColor: string;
  collarCustomColor: string;
  shoulderTrianglesCustomColor: string;
  sleeveCuffsCustomColor: string;

  collarStyle: CollarStyle;

  stripeCount: number;
  stripesEqualWidth: boolean;
  stripePattern: number[];

  skortStripeCount: number;
  skortStripesEqualWidth: boolean;
  skortStripePattern: number[];

  diagonalDirection: 'LEFT' | 'RIGHT';
  chevronSize: 'SMALL' | 'BIG';
  chevronDirection: 'UP' | 'DOWN';
  chevronPosition: 'TOP' | 'MIDDLE' | 'BOTTOM';
  sideTrianglesMode: 'TOP' | 'BOTTOM' | 'BOTH';
  skortSideTrianglesMode: 'TOP' | 'BOTTOM' | 'BOTH';
  shortsTrianglesMode: ShortsSideTrianglesPosition;
  sleevePattern: SleevePattern;

  girlsBottomType: GirlsBottomType;

  viewMode: ViewMode;
  editorStep: EditorStep;
  presets: KitPreset[];
  editingPresetId: string | null;
  currentPresetName: string;
  searchText: string;
  appliedSearchText: string;

  activeColorPicker: ColorFieldKey | null;

  sportIds: string[];
  selectedSports: { id: string; name: string }[];
  isSportsModalOpen: boolean;
  sportsMap: { [id: string]: string };
  sportsModalKitId: string | null;

  schoolGender: Gender | null;

  isLoading: boolean;
  isSaving: boolean;
  initialPayloadJson: string | null;
  isUnsavedModalOpen: boolean;
  pendingDeleteKitId: string | null;
  pendingDefaultKitId: string | null;
  defaultConflictName: string;
  kitNameError: string;
  saveError: string;
}

export class KitLab extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const defaults = getDefaultConfig();

    this.state = {
      ...defaults,
      girlsBottomType: 'SHORTS',
      viewMode: 'LIST',
      editorStep: 1,
      presets: [],
      editingPresetId: null,
      currentPresetName: '',
      searchText: '',
      appliedSearchText: '',
      activeColorPicker: null,
      sportIds: defaults.sportIds,
      selectedSports: [],
      isSportsModalOpen: false,
      sportsMap: {},
      sportsModalKitId: null,
      isLoading: false,
      isSaving: false,
      schoolGender: null,
      initialPayloadJson: null,
      isUnsavedModalOpen: false,
      pendingDeleteKitId: null,
      pendingDefaultKitId: null,
      defaultConflictName: '',
      kitNameError: '',
      saveError: ''
    };
  }

  async componentDidMount() {
    this.updateBreadcrumb();
    this.setState({ isLoading: true });
    try {
      await Promise.all([this.loadKits(), this.loadSportsMap(), this.loadSchoolGender()]);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.viewMode !== this.state.viewMode || prevState.editingPresetId !== this.state.editingPresetId) {
      this.updateBreadcrumb();
    }
  }

  componentWillUnmount() {
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
  }

  private updateBreadcrumb = () => {
    const { viewMode, editingPresetId } = this.state;
    const detail =
      viewMode === 'LIST'
        ? 'Sport & Fixtures / Kit lab'
        : `Sport & Fixtures / Kit lab / ${editingPresetId ? 'Edit kit' : 'Create kit'}`;
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb', { detail }));
  };

  private async loadSchoolGender() {
    const { user } = this.props;

    if (!user || !user.activeSchoolId) {
      return;
    }

    try {
      const school = await getSchool(user, user.activeSchoolId);
      const gender = (school && (school.gender as Gender)) || null;

      if (gender === 'MALE' || gender === 'FEMALE') {
        this.setState({ schoolGender: gender });
      }
    } catch (e) {}
  }

  private async loadKits() {
    const { user } = this.props;

    this.setState({ isLoading: true });

    try {
      const backendKits = await getKitLabKits(user);

      const presets: KitPreset[] = backendKits.map(kit => {
        const defaults = getDefaultConfig();

        let fromLayout: Partial<KitPreset> = {};
        if (kit.layoutPreset) {
          try {
            const parsed = JSON.parse(kit.layoutPreset);
            if (parsed && typeof parsed === 'object') {
              fromLayout = parsed as Partial<KitPreset>;
            }
          } catch (e) {}
        }

        const base: KitPreset = {
          id: kit._id,
          name: kit.name || 'Unnamed kit',
          ...defaults,
          ...fromLayout,
          sportIds: Array.isArray((kit as any).sportIds) ? (kit as any).sportIds.map((id: any) => id.toString()) : []
        };

        const gender: Gender = (kit.gender as Gender) || base.gender || defaults.gender;

        const bottomPattern = normalizeBottomPatternForGender(gender, base.bottomPattern);

        const skortSideTrianglesMode =
          (fromLayout as any).skortSideTrianglesMode ||
          (fromLayout as any).sideTrianglesMode ||
          defaults.skortSideTrianglesMode;

        const rawSleevePattern = (fromLayout as any).sleevePattern as string | undefined;

        let sleevePattern: SleevePattern;
        if (rawSleevePattern === 'TOP_STRIPE') {
          sleevePattern = 'TOP_TRIANGLES';
        } else if (rawSleevePattern === 'BOTTOM_STRIPE') {
          sleevePattern = 'TOP_STRIPE';
        } else if (rawSleevePattern === 'TOP_TRIANGLES' || rawSleevePattern === 'PLAIN') {
          sleevePattern = rawSleevePattern as SleevePattern;
        } else {
          sleevePattern = defaults.sleevePattern;
        }

        const preset: KitPreset = {
          ...base,
          gender,
          bottomPattern,
          skortSideTrianglesMode,
          sleevePattern,
          isDefaultBoys: gender === 'MALE' ? Boolean(kit.isDefault) : false,
          isDefaultGirls: gender === 'FEMALE' ? Boolean(kit.isDefault) : false
        };

        return preset;
      });

      this.setState({ presets, isLoading: false });
    } catch (e) {
      this.setState({ isLoading: false });
    }
  }

  private buildBackendPayloadFromCurrentState(): Partial<KitLabBackendKit> {
    const {
      gender,
      shirtPattern,
      bottomPattern,
      primaryColor,
      secondaryColor,
      shortsPrimaryColor,
      shortsSecondaryColor,
      showPocket,
      showCollar,
      showSleeves,
      showShoulderTriangles,
      showSleeveCuffs,
      pocketColorSource,
      sleevesColorSource,
      collarColorSource,
      shoulderTrianglesColorSource,
      sleeveCuffsColorSource,
      pocketCustomColor,
      sleevesCustomColor,
      collarCustomColor,
      shoulderTrianglesCustomColor,
      sleeveCuffsCustomColor,
      collarStyle,
      stripeCount,
      stripesEqualWidth,
      stripePattern,
      skortStripeCount,
      skortStripesEqualWidth,
      skortStripePattern,
      diagonalDirection,
      chevronSize,
      chevronDirection,
      chevronPosition,
      sideTrianglesMode,
      skortSideTrianglesMode,
      shortsTrianglesMode,
      sleevePattern,
      currentPresetName,
      sportIds
    } = this.state;

    const layoutPresetObj = {
      shirtPattern,
      bottomPattern,
      primaryColor,
      secondaryColor,
      shortsPrimaryColor,
      shortsSecondaryColor,
      showPocket,
      showCollar,
      showSleeves,
      showShoulderTriangles,
      showSleeveCuffs,
      pocketColorSource,
      sleevesColorSource,
      collarColorSource,
      shoulderTrianglesColorSource,
      sleeveCuffsColorSource,
      pocketCustomColor,
      sleevesCustomColor,
      collarCustomColor,
      shoulderTrianglesCustomColor,
      sleeveCuffsCustomColor,
      collarStyle,
      stripeCount,
      stripesEqualWidth,
      stripePattern,
      skortStripeCount,
      skortStripesEqualWidth,
      skortStripePattern,
      diagonalDirection,
      chevronSize,
      chevronDirection,
      chevronPosition,
      sideTrianglesMode,
      skortSideTrianglesMode,
      sleevePattern,
      shortsTrianglesMode
    };

    const segments = [
      { segmentKey: 'SHIRT_PRIMARY', color: primaryColor },
      { segmentKey: 'SHIRT_SECONDARY', color: secondaryColor },
      { segmentKey: 'SHORTS_PRIMARY', color: shortsPrimaryColor },
      { segmentKey: 'SHORTS_SECONDARY', color: shortsSecondaryColor }
    ];

    return {
      name: this.normalizeKitName(currentPresetName),
      gender,
      layoutPreset: JSON.stringify(layoutPresetObj),
      segments,
      sportIds: this.normalizeSportIds(sportIds)
    };
  }

  private normalizeKitName = (name: string): string => name.trim().replace(/\s+/g, ' ');

  private normalizeSportIds = (sportIds?: string[]): string[] =>
    Array.from(new Set((sportIds || []).filter(Boolean).map(id => id.toString()))).sort();

  private getKitNameValidationError = (): string => {
    const { currentPresetName, gender, sportIds, presets, editingPresetId } = this.state;
    const name = this.normalizeKitName(currentPresetName);

    if (!name) {
      return 'Enter a kit name.';
    }

    const normalizedName = name.toLowerCase();
    const normalizedSportIds = this.normalizeSportIds(sportIds);
    const duplicate = presets.find(preset => {
      if (preset.id === editingPresetId || preset.gender !== gender) return false;
      if (this.normalizeKitName(preset.name || '').toLowerCase() !== normalizedName) return false;

      const presetSportIds = this.normalizeSportIds(preset.sportIds);
      return (
        presetSportIds.length === normalizedSportIds.length &&
        presetSportIds.every((id, index) => id === normalizedSportIds[index])
      );
    });

    return duplicate ? 'A kit with this name, gender and sports already exists.' : '';
  };

  private validateKitName = () => {
    this.setState({ kitNameError: this.getKitNameValidationError() });
  };

  onGenderChange = (nextGender: Gender) => {
    this.setState(prev => {
      let nextBottomPattern = normalizeBottomPatternForGender(nextGender, prev.bottomPattern);
      let girlsBottomType: GirlsBottomType = prev.girlsBottomType;

      if (nextGender === 'FEMALE') {
        if (isSkortPatternId(prev.bottomPattern)) {
          girlsBottomType = 'SKORT';
          nextBottomPattern = prev.bottomPattern;
        } else {
          girlsBottomType = 'SKORT';
          nextBottomPattern = 'SKORT_PLAIN';
        }
      } else {
        girlsBottomType = 'SHORTS';
      }

      return {
        gender: nextGender,
        bottomPattern: nextBottomPattern,
        girlsBottomType,
        kitNameError: '',
        saveError: ''
      };
    });
  };

  onGirlsBottomTypeChange = (mode: GirlsBottomType) => {
    this.setState(prev => {
      let nextPattern = prev.bottomPattern;

      if (mode === 'SKORT') {
        if (!isSkortPatternId(nextPattern)) {
          nextPattern = 'SKORT_PLAIN';
        }
      } else {
        if (isSkortPatternId(nextPattern)) {
          nextPattern = 'SHORTS_PLAIN';
        }
      }

      return {
        girlsBottomType: mode,
        bottomPattern: nextPattern
      };
    });
  };

  onShirtPatternClick = (patternId: KitPatternVariant) => {
    this.setState({
      shirtPattern: patternId
    });
  };

  onBottomPatternClick = (patternId: KitBottomPattern) => {
    this.setState({
      bottomPattern: patternId
    });
  };

  onPrimaryColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      primaryColor: event.target.value
    });
  };

  onSecondaryColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      secondaryColor: event.target.value
    });
  };

  onDetailColorSourceChange = (
    detail: 'POCKET' | 'SLEEVES' | 'COLLAR' | 'SHOULDER_TRIANGLES' | 'SLEEVE_CUFFS',
    source: DetailColorSource
  ) => {
    if (detail === 'POCKET') {
      this.setState({ pocketColorSource: source });
    } else if (detail === 'SLEEVES') {
      this.setState({ sleevesColorSource: source });
    } else if (detail === 'COLLAR') {
      this.setState({ collarColorSource: source });
    } else if (detail === 'SHOULDER_TRIANGLES') {
      this.setState({ shoulderTrianglesColorSource: source });
    } else if (detail === 'SLEEVE_CUFFS') {
      this.setState({ sleeveCuffsColorSource: source });
    }
  };

  onDetailCustomColorChange = (
    detail: 'POCKET' | 'SLEEVES' | 'COLLAR' | 'SHOULDER_TRIANGLES' | 'SLEEVE_CUFFS',
    value: string
  ) => {
    if (detail === 'POCKET') {
      this.setState({ pocketCustomColor: value });
    } else if (detail === 'SLEEVES') {
      this.setState({ sleevesCustomColor: value });
    } else if (detail === 'COLLAR') {
      this.setState({ collarCustomColor: value });
    } else if (detail === 'SHOULDER_TRIANGLES') {
      this.setState({ shoulderTrianglesCustomColor: value });
    } else if (detail === 'SLEEVE_CUFFS') {
      this.setState({ sleeveCuffsCustomColor: value });
    }
  };

  onTogglePocket = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      showPocket: event.target.checked
    });
  };

  onToggleCollar = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      showCollar: event.target.checked
    });
  };

  onToggleSleeves = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      showSleeves: event.target.checked
    });
  };

  onToggleShoulderTriangles = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ showShoulderTriangles: event.target.checked });
  };

  onToggleSleeveCuffs = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ showSleeveCuffs: event.target.checked });
  };

  onShirtStripeCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = parseInt(event.target.value, 10);
    if (Number.isNaN(raw)) {
      this.setState({ stripeCount: 5 });
      return;
    }

    const clamped = Math.max(2, Math.min(raw, 12));

    this.setState(prev => {
      let pattern = prev.stripePattern.slice();

      if (pattern.length < clamped) {
        const start = pattern.length;
        for (let i = start; i < clamped; i += 1) {
          pattern.push(i % 2 === 0 ? 2 : 1);
        }
      } else if (pattern.length > clamped) {
        pattern = pattern.slice(0, clamped);
      }

      return {
        stripeCount: clamped,
        stripePattern: pattern
      };
    });
  };

  onShirtStripesEqualWidthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    this.setState(prev => {
      const count = prev.stripeCount;
      let pattern = prev.stripePattern;

      if (!pattern || pattern.length !== count) {
        pattern = Array.from({ length: count }, (_, i) => (i % 2 === 0 ? 2 : 1));
      }

      return {
        stripesEqualWidth: checked,
        stripePattern: pattern
      };
    });
  };

  onShirtStripeChipToggle = (index: number) => {
    this.setState(prev => {
      const count = prev.stripeCount;
      const pattern = prev.stripePattern.slice();

      while (pattern.length < count) {
        pattern.push(1);
      }

      pattern[index] = pattern[index] > 1 ? 1 : 2;

      return { stripePattern: pattern };
    });
  };

  onSkortStripeCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = parseInt(event.target.value, 10);
    if (Number.isNaN(raw)) {
      this.setState({ skortStripeCount: 5 });
      return;
    }

    const clamped = Math.max(2, Math.min(raw, 12));

    this.setState(prev => {
      let pattern = prev.skortStripePattern.slice();

      if (pattern.length < clamped) {
        const start = pattern.length;
        for (let i = start; i < clamped; i += 1) {
          pattern.push(i % 2 === 0 ? 2 : 1);
        }
      } else if (pattern.length > clamped) {
        pattern = pattern.slice(0, clamped);
      }

      return {
        skortStripeCount: clamped,
        skortStripePattern: pattern
      };
    });
  };

  onSkortStripesEqualWidthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    this.setState(prev => {
      const count = prev.skortStripeCount;
      let pattern = prev.skortStripePattern;

      if (!pattern || pattern.length !== count) {
        pattern = Array.from({ length: count }, (_, i) => (i % 2 === 0 ? 2 : 1));
      }

      return {
        skortStripesEqualWidth: checked,
        skortStripePattern: pattern
      };
    });
  };

  onSkortStripeChipToggle = (index: number) => {
    this.setState(prev => {
      const count = prev.skortStripeCount;
      const pattern = prev.skortStripePattern.slice();

      while (pattern.length < count) {
        pattern.push(1);
      }

      pattern[index] = pattern[index] > 1 ? 1 : 2;

      return { skortStripePattern: pattern };
    });
  };

  onDiagonalDirectionChange = (dir: 'LEFT' | 'RIGHT') => {
    this.setState({ diagonalDirection: dir });
  };

  onChevronSizeChange = (size: 'SMALL' | 'BIG') => {
    this.setState({ chevronSize: size });
  };

  onChevronDirectionChange = (dir: 'UP' | 'DOWN') => {
    this.setState({ chevronDirection: dir });
  };

  onChevronPositionChange = (pos: 'TOP' | 'MIDDLE' | 'BOTTOM') => {
    this.setState({ chevronPosition: pos });
  };

  onSideTrianglesModeChange = (mode: 'TOP' | 'BOTTOM' | 'BOTH') => {
    this.setState({ sideTrianglesMode: mode });
  };

  onSkortSideTrianglesModeChange = (mode: 'TOP' | 'BOTTOM' | 'BOTH') => {
    this.setState({ skortSideTrianglesMode: mode });
  };

  onShortsTrianglesModeChange = (mode: ShortsSideTrianglesPosition) => {
    this.setState({ shortsTrianglesMode: mode });
  };

  onShortsPrimaryColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      shortsPrimaryColor: event.target.value
    });
  };

  onShortsSecondaryColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      shortsSecondaryColor: event.target.value
    });
  };

  onCurrentPresetNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ currentPresetName: event.target.value, kitNameError: '', saveError: '' });
  };

  private async loadSportsMap() {
    const { user } = this.props;

    try {
      const [allSportsRaw, favoriteSportsRaw] = await Promise.all([
        Promise.resolve(searchFunctionSportsWithFavorite(user, '', false, 600)),
        Promise.resolve(searchFunctionSportsWithFavorite(user, '', true, 600))
      ]);

      const allSports = Array.isArray(allSportsRaw) ? allSportsRaw : [];
      const favoriteSports = Array.isArray(favoriteSportsRaw) ? favoriteSportsRaw : [];

      const merged = [...allSports, ...favoriteSports];

      const sportsMap: { [id: string]: string } = {};

      (merged || []).forEach((sport: any) => {
        if (!sport) return;

        const name = sport.name || getName(sport);
        if (!name) return;

        const idCandidates: any[] = [
          sport.id,
          sport._id,
          sport.sportId,
          propz.get(sport, ['sportId']),
          propz.get(sport, ['sport', 'id']),
          propz.get(sport, ['sport', '_id']),
          propz.get(sport, ['sport', 'sportId'])
        ];

        idCandidates
          .filter(Boolean)
          .map(v => v.toString())
          .forEach(key => {
            if (!sportsMap[key]) {
              sportsMap[key] = name;
            }
          });
      });

      this.setState({ sportsMap });
    } catch (e) {}
  }

  private openKitSportsModal = (kitId: string) => {
    this.setState({ sportsModalKitId: kitId });
  };

  private closeKitSportsModal = () => {
    this.setState({ sportsModalKitId: null });
  };

  private openColorPicker = (field: ColorFieldKey) => {
    this.setState({ activeColorPicker: field });
  };

  private closeColorPicker = () => {
    this.setState({ activeColorPicker: null });
  };

  private handleSketchChange = (field: ColorFieldKey, color: any) => {
    const hex = color && color.hex ? color.hex : '#000000';

    switch (field) {
      case 'PRIMARY':
        this.setState({ primaryColor: hex });
        break;
      case 'SECONDARY':
        this.setState({ secondaryColor: hex });
        break;
      case 'SHORTS_PRIMARY':
        this.setState({ shortsPrimaryColor: hex });
        break;
      case 'SHORTS_SECONDARY':
        this.setState({ shortsSecondaryColor: hex });
        break;
      case 'SLEEVES_CUSTOM':
        this.setState({ sleevesCustomColor: hex });
        break;
      case 'POCKET_CUSTOM':
        this.setState({ pocketCustomColor: hex });
        break;
      case 'COLLAR_CUSTOM':
        this.setState({ collarCustomColor: hex });
        break;
      case 'SHOULDER_TRIANGLES_CUSTOM':
        this.setState({ shoulderTrianglesCustomColor: hex });
        break;
      case 'SLEEVE_CUFFS_CUSTOM':
        this.setState({ sleeveCuffsCustomColor: hex });
        break;
    }
  };

  private setEditorStep = (step: EditorStep) => {
    this.setState({ editorStep: step });
  };

  private startCreateNew = () => {
    const defaults = getDefaultConfig();
    const { schoolGender } = this.state;

    let gender: Gender = schoolGender || defaults.gender;
    let bottomPattern: KitBottomPattern = defaults.bottomPattern;
    let girlsBottomType: GirlsBottomType = 'SHORTS';

    if (gender === 'FEMALE') {
      girlsBottomType = 'SKORT';
      bottomPattern = 'SKORT_PLAIN';
    } else {
      gender = 'MALE';
      bottomPattern = normalizeBottomPatternForGender('MALE', bottomPattern);
      girlsBottomType = 'SHORTS';
    }

    this.setState(
      {
        ...defaults,
        gender,
        bottomPattern,
        girlsBottomType,
        viewMode: 'EDIT',
        editorStep: 1,
        editingPresetId: null,
        currentPresetName: '',
        activeColorPicker: null,
        sportIds: defaults.sportIds,
        selectedSports: [],
        isSportsModalOpen: false,
        initialPayloadJson: null,
        kitNameError: '',
        saveError: ''
      },
      () => {
        const initialPayload = this.buildBackendPayloadFromCurrentState();
        this.setState({ initialPayloadJson: JSON.stringify(initialPayload) });
      }
    );
  };

  private startEdit = (presetId: string) => {
    const { presets } = this.state;
    const preset = presets.find(p => p.id === presetId);
    if (!preset) return;

    const girlsBottomType: GirlsBottomType =
      preset.gender === 'FEMALE' && isSkortPatternId(preset.bottomPattern) ? 'SKORT' : 'SHORTS';

    this.setState(
      {
        viewMode: 'EDIT',
        editorStep: 1,
        editingPresetId: preset.id,
        currentPresetName: preset.name,

        gender: preset.gender,
        shirtPattern: preset.shirtPattern,
        bottomPattern: preset.bottomPattern,

        primaryColor: preset.primaryColor,
        secondaryColor: preset.secondaryColor,
        shortsPrimaryColor: preset.shortsPrimaryColor,
        shortsSecondaryColor: preset.shortsSecondaryColor,

        showPocket: preset.showPocket,
        showCollar: preset.showCollar,
        showSleeves: preset.showSleeves,
        showShoulderTriangles: preset.showShoulderTriangles,
        showSleeveCuffs: preset.showSleeveCuffs,

        collarStyle: preset.collarStyle,

        pocketColorSource: preset.pocketColorSource,
        sleevesColorSource: preset.sleevesColorSource,
        collarColorSource: preset.collarColorSource,
        shoulderTrianglesColorSource: preset.shoulderTrianglesColorSource,
        sleeveCuffsColorSource: preset.sleeveCuffsColorSource,

        pocketCustomColor: preset.pocketCustomColor,
        sleevesCustomColor: preset.sleevesCustomColor,
        collarCustomColor: preset.collarCustomColor,
        shoulderTrianglesCustomColor: preset.shoulderTrianglesCustomColor,
        sleeveCuffsCustomColor: preset.sleeveCuffsCustomColor,

        stripeCount: preset.stripeCount,
        stripesEqualWidth: preset.stripesEqualWidth,
        stripePattern: preset.stripePattern.slice(),
        diagonalDirection: preset.diagonalDirection,
        chevronSize: preset.chevronSize,
        chevronDirection: preset.chevronDirection,
        chevronPosition: preset.chevronPosition,
        sideTrianglesMode: preset.sideTrianglesMode,
        skortSideTrianglesMode: preset.skortSideTrianglesMode,
        shortsTrianglesMode: preset.shortsTrianglesMode,
        sleevePattern: preset.sleevePattern || 'PLAIN',
        girlsBottomType,
        sportIds: preset.sportIds.slice(),
        selectedSports: [],
        isSportsModalOpen: false,
        initialPayloadJson: null,
        kitNameError: '',
        saveError: ''
      },
      () => {
        const initialPayload = this.buildBackendPayloadFromCurrentState();
        this.setState({ initialPayloadJson: JSON.stringify(initialPayload) });
      }
    );
  };

  private handleCancelDiscardChanges = () => {
    this.setState({ isUnsavedModalOpen: false });
  };

  private handleConfirmDiscardChanges = () => {
    this.setState({
      isUnsavedModalOpen: false,
      viewMode: 'LIST',
      editingPresetId: null,
      initialPayloadJson: null
    });
  };

  private hasUnsavedChanges = (): boolean => {
    const { viewMode, initialPayloadJson } = this.state;
    if (viewMode !== 'EDIT') return false;
    if (!initialPayloadJson) return false;

    const currentPayload = this.buildBackendPayloadFromCurrentState();
    const currentJson = JSON.stringify(currentPayload);

    return currentJson !== initialPayloadJson;
  };

  private handleBackToList = () => {
    if (this.hasUnsavedChanges()) {
      this.setState({ isUnsavedModalOpen: true });
      return;
    }

    this.setState({
      viewMode: 'LIST',
      editingPresetId: null,
      initialPayloadJson: null
    });
  };

  private requestDelete = (presetId: string) => {
    this.setState({ pendingDeleteKitId: presetId });
  };

  private closeDeleteModal = () => {
    this.setState({ pendingDeleteKitId: null });
  };

  private confirmDelete = async () => {
    const { user } = this.props;
    const { pendingDeleteKitId } = this.state;
    if (!pendingDeleteKitId) return;

    this.setState({ isSaving: true });
    try {
      await deleteKitLabKit(user, pendingDeleteKitId);
      await this.loadKits();
    } finally {
      this.setState({ isSaving: false, pendingDeleteKitId: null });
    }
  };

  private applyDefaultKit = async (presetId: string) => {
    const { user } = this.props;
    this.setState({ isSaving: true, pendingDefaultKitId: null, defaultConflictName: '' });
    try {
      await setKitLabDefaultKit(user, presetId);
      await this.loadKits();
    } finally {
      this.setState({ isSaving: false });
    }
  };

  private handleSetDefaultBoys = async (presetId: string) => {
    const { presets } = this.state;

    const target = presets.find(p => p.id === presetId);
    if (!target) return;

    const conflict = presets.find(
      p => p.id !== presetId && p.gender === 'MALE' && p.isDefaultBoys && this.haveSameSports(p, target)
    );

    if (conflict) {
      this.setState({ pendingDefaultKitId: presetId, defaultConflictName: conflict.name });
      return;
    }

    await this.applyDefaultKit(presetId);
  };

  private handleSetDefaultGirls = async (presetId: string) => {
    const { presets } = this.state;

    const target = presets.find(p => p.id === presetId);
    if (!target) return;

    const conflict = presets.find(
      p => p.id !== presetId && p.gender === 'FEMALE' && p.isDefaultGirls && this.haveSameSports(p, target)
    );

    if (conflict) {
      this.setState({ pendingDefaultKitId: presetId, defaultConflictName: conflict.name });
      return;
    }

    await this.applyDefaultKit(presetId);
  };

  private handleSavePreset = async () => {
    const { user } = this.props;
    const { editingPresetId } = this.state;
    const kitNameError = this.getKitNameValidationError();

    if (kitNameError) {
      this.setState({ kitNameError, saveError: '' }, () => {
        const nameInput = document.getElementById('kitLabPresetName') as HTMLInputElement | null;
        if (nameInput) nameInput.focus();
      });
      return;
    }

    const payload = this.buildBackendPayloadFromCurrentState();

    this.setState({ isSaving: true, kitNameError: '', saveError: '' });

    try {
      if (editingPresetId) {
        await updateKitLabKit(user, editingPresetId, payload);
      } else {
        await createKitLabKit(user, payload);
      }

      await this.loadKits();

      this.setState({
        viewMode: 'LIST',
        editingPresetId: null,
        initialPayloadJson: null
      });
    } catch (error) {
      const status = propz.get(error, ['response', 'status']);
      const serverMessage = propz.get(error, ['response', 'data', 'details', 'text']);

      if (status === 409 || (status === 400 && typeof serverMessage === 'string' && /kit name/i.test(serverMessage))) {
        this.setState({
          kitNameError:
            typeof serverMessage === 'string' && serverMessage
              ? serverMessage
              : 'A kit with this name, gender and sports already exists.'
        });
      } else {
        this.setState({ saveError: 'The kit could not be saved. Please try again.' });
      }
    } finally {
      this.setState({ isSaving: false });
    }
  };

  private renderDeleteConfirmation() {
    const { pendingDeleteKitId, presets } = this.state;
    if (!pendingDeleteKitId) return null;
    const preset = presets.find(item => item.id === pendingDeleteKitId);
    const kitName = preset && preset.name ? preset.name : 'Unnamed kit';

    return (
      <SimpleModal
        isOpen={true}
        title="Delete kit"
        body={
          <div className="eKitLab__deleteConfirmContent">
            <span className="eKitLab__deleteConfirmIcon" aria-hidden="true">
              <FontAwesomeIcon icon={faTrash} />
            </span>
            <p>Are you sure you want to delete “{kitName}”? This action cannot be undone.</p>
          </div>
        }
        isBodyBold={false}
        buttonText="Delete kit"
        buttonCancelText="Cancel"
        buttonDisabled={this.state.isSaving}
        onButtonClick={this.confirmDelete}
        onCloseClick={this.closeDeleteModal}
        customClass="eKitLab__confirmModal eKitLab__confirmModal--danger"
        dialogStyle={{ maxWidth: 420 }}
      />
    );
  }

  private closeDefaultConflictModal = () => {
    this.setState({ pendingDefaultKitId: null, defaultConflictName: '' });
  };

  private confirmDefaultReplacement = () => {
    const { pendingDefaultKitId } = this.state;
    if (pendingDefaultKitId) {
      this.applyDefaultKit(pendingDefaultKitId);
    }
  };

  private renderDefaultConflictModal() {
    const { pendingDefaultKitId, defaultConflictName } = this.state;
    if (!pendingDefaultKitId) return null;

    return (
      <SimpleModal
        isOpen={true}
        title="Replace default kit?"
        body={`“${defaultConflictName}” is already the default for the same sport selection. Continuing will replace it.`}
        buttonText="Replace default"
        buttonDisabled={this.state.isSaving}
        onButtonClick={this.confirmDefaultReplacement}
        onCloseClick={this.closeDefaultConflictModal}
        customClass="eKitLab__confirmModal"
      />
    );
  }

  private renderListView() {
    const { presets, isLoading, isSaving, sportsMap, sportsModalKitId, searchText, appliedSearchText } = this.state;

    const modalKit = sportsModalKitId ? presets.find(p => p.id === sportsModalKitId) : null;
    const normalizedSearch = appliedSearchText.trim().toLowerCase();
    const visiblePresets = normalizedSearch
      ? presets.filter(preset => {
          const matchesName = (preset.name || '').toLowerCase().includes(normalizedSearch);
          const matchesSport = (preset.sportIds || []).some(id =>
            (sportsMap[id.toString()] || '').toLowerCase().includes(normalizedSearch)
          );
          return matchesName || matchesSport;
        })
      : presets;

    return (
      <div className="container-fluid eKitLab eKitLab--list">
        {this.renderDeleteConfirmation()}
        {this.renderDefaultConflictModal()}
        <header className="eKitLab__pageHeader eKitLab__pageHeader--list">
          <div className="eKitLab__headerCopy">
            <div className="eKitLab__listHeading">
              <h1 className="eKitLab__title">Kit lab</h1>
              <span className="eKitLab__count">
                {presets.length} {presets.length === 1 ? 'kit' : 'kits'}
              </span>
            </div>
            <p className="eKitLab__subtitle">Create and manage the kits used by your teams and fixtures.</p>
          </div>
          <button
            type="button"
            className="eKitLab__button eKitLab__button--primary"
            onClick={this.startCreateNew}
            disabled={isSaving}
          >
            <FontAwesomeIcon icon={faPlus} />
            Create kit
          </button>
        </header>

        <section className="eKitLab__listContent">
          <div className="eKitLab__listToolbar">
            <label className="eKitLab__search">
              <span>Kit name or sport</span>
              <input
                type="text"
                value={searchText}
                placeholder="Search by kit name or sport"
                aria-label="Search by kit name or sport"
                onChange={event => this.setState({ searchText: event.target.value })}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    this.setState({ appliedSearchText: searchText });
                  }
                }}
              />
              {searchText && (
                <button
                  type="button"
                  className="eKitLab__searchClear"
                  onClick={() => this.setState({ searchText: '', appliedSearchText: '' })}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </label>
            {normalizedSearch && (
              <span className="eKitLab__resultCount">
                {visiblePresets.length} {visiblePresets.length === 1 ? 'result' : 'results'}
              </span>
            )}
          </div>

          {isLoading && <div className="eKitLab__emptyState">Loading kits…</div>}

          {!isLoading && presets.length === 0 && (
            <ListEmptyState
              icon={faTshirt}
              title="No saved kits yet"
              description="Create your first kit to reuse its colours and design across events and teams."
            />
          )}

          {!isLoading && presets.length > 0 && visiblePresets.length === 0 && (
            <ListEmptyState
              icon={faTshirt}
              title="No kits match your search"
              description="Try another kit name or sport, or clear the search to see every saved kit."
            />
          )}

          <div className="row eKitLab__presetGrid">
            {visiblePresets.map(preset => {
              const isGirls = preset.gender === 'FEMALE';
              const isDefault = isGirls ? preset.isDefaultGirls : preset.isDefaultBoys;

              const effectiveSecondary = preset.secondaryColor;
              const effectiveShortsSecondary = preset.shortsSecondaryColor;

              const resolveDetailColorFromPreset = (
                which: 'POCKET' | 'SLEEVES' | 'COLLAR' | 'SHOULDER_TRIANGLES' | 'SLEEVE_CUFFS'
              ): string => {
                const { primaryColor, secondaryColor } = preset;

                let source: DetailColorSource;
                let custom: string;

                if (which === 'POCKET') {
                  source = preset.pocketColorSource;
                  custom = preset.pocketCustomColor;
                } else if (which === 'SLEEVES') {
                  source = preset.sleevesColorSource;
                  custom = preset.sleevesCustomColor;
                } else if (which === 'COLLAR') {
                  source = preset.collarColorSource;
                  custom = preset.collarCustomColor;
                } else if (which === 'SHOULDER_TRIANGLES') {
                  source = preset.shoulderTrianglesColorSource;
                  custom = preset.shoulderTrianglesCustomColor;
                } else {
                  source = preset.sleeveCuffsColorSource;
                  custom = preset.sleeveCuffsCustomColor;
                }

                if (source === 'PRIMARY') return primaryColor;
                if (source === 'SECONDARY') return secondaryColor;
                return custom || secondaryColor || primaryColor;
              };

              const pocketColor = resolveDetailColorFromPreset('POCKET');
              const sleevesColor = resolveDetailColorFromPreset('SLEEVES');
              const collarColor = resolveDetailColorFromPreset('COLLAR');
              const shoulderTrianglesColor = resolveDetailColorFromPreset('SHOULDER_TRIANGLES');
              const sleeveCuffsColor = resolveDetailColorFromPreset('SLEEVE_CUFFS');

              const sports = (preset.sportIds || []).map(id => {
                const key = id.toString();
                const name = sportsMap[key] || 'Unknown sport';
                return { id: key, name };
              });

              const totalSports = sports.length;
              const visibleSports = sports.slice(0, 3);

              return (
                <div className="col-xl-3 col-lg-4 col-md-6 mb-4" key={preset.id}>
                  <div className="eKitLab__presetCard">
                    <div className="eKitLab__presetHeader">
                      <div className="eKitLab__presetTitle">{preset.name || 'Unnamed kit'}</div>
                      <div className="eKitLab__presetMeta">
                        <span className="eKitLab__genderBadge">{isGirls ? 'Girls kit' : 'Boys kit'}</span>
                        {isDefault && (
                          <span className="eKitLab__defaultBadge">
                            <FontAwesomeIcon icon={faStar} /> Default
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="eKitLab__presetPreview eKitLab__presetPreview--compact">
                      <div className="eKitLab__presetPreviewInner">
                        <KitShirtPreview
                          variant={preset.shirtPattern}
                          primaryColor={preset.primaryColor}
                          secondaryColor={effectiveSecondary}
                          showPocket={preset.showPocket}
                          showCollar={preset.showCollar}
                          showSleeves={preset.showSleeves}
                          pocketColor={pocketColor}
                          sleevesColor={sleevesColor}
                          collarColor={collarColor}
                          shoulderTrianglesColor={shoulderTrianglesColor}
                          sleeveCuffsColor={sleeveCuffsColor}
                          stripeCount={preset.stripeCount}
                          stripesEqualWidth={preset.stripesEqualWidth}
                          stripePattern={preset.stripePattern}
                          diagonalDirection={preset.diagonalDirection}
                          chevronSize={preset.chevronSize}
                          chevronDirection={preset.chevronDirection}
                          chevronPosition={preset.chevronPosition}
                          sideTrianglesMode={preset.sideTrianglesMode}
                          showShoulderTriangles={preset.showShoulderTriangles}
                          showSleeveCuffs={preset.showSleeveCuffs}
                          collarStyle={preset.collarStyle}
                          sleevePattern={preset.sleevePattern}
                          className="eKitLab__shirtSvg--tiny"
                        />
                        {isSkortPatternId(preset.bottomPattern) ? (
                          <KitSkortPreview
                            pattern={mapBottomToSkortPattern(preset.bottomPattern)}
                            primaryColor={preset.shortsPrimaryColor}
                            secondaryColor={preset.shortsSecondaryColor}
                            stripeCount={preset.skortStripeCount}
                            stripesEqualWidth={preset.skortStripesEqualWidth}
                            stripePattern={preset.skortStripePattern}
                            sideTrianglesMode={preset.skortSideTrianglesMode}
                            className="eKitLab__bottomSvg--tiny eKitLab__bottomSvg--tinySkort"
                          />
                        ) : (
                          <KitShortsPreview
                            variant={
                              preset.bottomPattern === 'SHORTS_SIDE'
                                ? 'sideStripes'
                                : preset.bottomPattern === 'SHORTS_TRIANGLES'
                                ? 'sideTriangles'
                                : preset.bottomPattern === 'SHORTS_BIG_TRIANGLES'
                                ? 'sideBigTriangles'
                                : preset.bottomPattern === 'SHORTS_SEMICIRCLES'
                                ? 'sideSemiCircles'
                                : 'plain'
                            }
                            primaryColor={preset.shortsPrimaryColor}
                            secondaryColor={effectiveShortsSecondary}
                            sideTrianglesPosition={preset.shortsTrianglesMode}
                            className="eKitLab__bottomSvg--tiny"
                          />
                        )}
                      </div>
                    </div>

                    <div className="eKitLab__presetSports">
                      {totalSports > 0 ? (
                        <>
                          {visibleSports.map(sport => (
                            <span key={sport.id} className="eKitLab__sportBadge">
                              {sport.name}
                            </span>
                          ))}

                          {totalSports > visibleSports.length && (
                            <button
                              type="button"
                              className="eKitLab__sportMoreBtn"
                              onClick={() => this.openKitSportsModal(preset.id)}
                            >
                              +{totalSports - visibleSports.length} more
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="eKitLab__sportBadge eKitLab__sportBadge--all">
                          All sports &amp; activities
                        </span>
                      )}
                    </div>
                    <div className="eKitLab__presetActions">
                      <button
                        type="button"
                        className="eKitLab__iconBtn"
                        onClick={() => this.startEdit(preset.id)}
                        title="Edit kit"
                        disabled={isSaving}
                      >
                        <FontAwesomeIcon icon={faPen} />
                        <span className="sr-only">Edit kit</span>
                      </button>

                      <button
                        type="button"
                        className="eKitLab__iconBtn eKitLab__iconBtn--danger"
                        onClick={() => this.requestDelete(preset.id)}
                        title="Delete kit"
                        disabled={isSaving}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        <span className="sr-only">Delete kit</span>
                      </button>

                      {!isGirls && (
                        <button
                          type="button"
                          className={
                            'eKitLab__iconBtn eKitLab__iconBtn--star ' +
                            (preset.isDefaultBoys ? 'eKitLab__iconBtn--active' : '')
                          }
                          onClick={() => this.handleSetDefaultBoys(preset.id)}
                          title={preset.isDefaultBoys ? 'Default boys kit' : 'Set default boys kit'}
                          aria-pressed={Boolean(preset.isDefaultBoys)}
                          disabled={isSaving || Boolean(preset.isDefaultBoys)}
                        >
                          <FontAwesomeIcon icon={preset.isDefaultBoys ? faStar : faStarRegular} />
                          <span className="sr-only">
                            {preset.isDefaultBoys ? 'Default boys kit' : 'Set as default boys kit'}
                          </span>
                        </button>
                      )}

                      {isGirls && (
                        <button
                          type="button"
                          className={
                            'eKitLab__iconBtn eKitLab__iconBtn--star ' +
                            (preset.isDefaultGirls ? 'eKitLab__iconBtn--active' : '')
                          }
                          onClick={() => this.handleSetDefaultGirls(preset.id)}
                          title={preset.isDefaultGirls ? 'Default girls kit' : 'Set default girls kit'}
                          aria-pressed={Boolean(preset.isDefaultGirls)}
                          disabled={isSaving || Boolean(preset.isDefaultGirls)}
                        >
                          <FontAwesomeIcon icon={preset.isDefaultGirls ? faStar : faStarRegular} />
                          <span className="sr-only">
                            {preset.isDefaultGirls ? 'Default girls kit' : 'Set as default girls kit'}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {modalKit && (
          <SimpleModal
            isOpen={true}
            title={`Sports for "${modalKit.name || 'Unnamed kit'}"`}
            onCloseClick={this.closeKitSportsModal}
            showFooter={false}
            customClass="eKitLab__sportsListModal"
          >
            <ul className="eKitLab__sportsList">
              {(modalKit.sportIds || []).map(id => {
                const key = id.toString();
                const name = sportsMap[key] || key;
                return <li key={key}>{name}</li>;
              })}
            </ul>

            <div className="text-right">
              <button
                type="button"
                className="eKitLab__button eKitLab__button--secondary"
                onClick={this.closeKitSportsModal}
              >
                Close
              </button>
            </div>
          </SimpleModal>
        )}
      </div>
    );
  }

  private getSportsForKit = (text: string) => {
    const { user } = this.props;
    const { sportIds } = this.state;

    return Promise.resolve(searchFunctionSportsWithFavorite(user, text, false)).then((sports: any[]) => {
      if (!Array.isArray(sports) || !sportIds || sportIds.length === 0) {
        return sports;
      }

      const selectedIds = new Set(sportIds.map(id => id.toString()));

      return sports.filter(sport => {
        const id = (sport.id || sport._id || '').toString();
        return !selectedIds.has(id);
      });
    });
  };

  private openSportsModal = () => {
    this.setState({ isSportsModalOpen: true });
  };

  private closeSportsModal = () => {
    this.setState({ isSportsModalOpen: false });
  };

  private handleSportSelected = (sport: any) => {
    const rawId =
      sport.sportId || propz.get(sport, ['sport', 'id']) || propz.get(sport, ['sport', '_id']) || sport.id || sport._id;

    const id = rawId && rawId.toString();
    const name = sport.name || getName(sport);

    if (!id) return;

    this.setState(prev => {
      if (prev.sportIds.includes(id)) {
        return null;
      }

      const nextSelectedSports = [...prev.selectedSports, { id, name }];

      const nextSportsMap = { ...prev.sportsMap };
      if (name && !nextSportsMap[id]) {
        nextSportsMap[id] = name;
      }

      return {
        sportIds: [...prev.sportIds, id],
        selectedSports: nextSelectedSports,
        sportsMap: nextSportsMap,
        kitNameError: '',
        saveError: ''
      };
    });
  };

  private handleRemoveSport = (sportId: string) => {
    this.setState(prev => ({
      sportIds: prev.sportIds.filter(id => id !== sportId),
      selectedSports: prev.selectedSports.filter(s => s.id !== sportId),
      kitNameError: '',
      saveError: ''
    }));
  };

  private clearSports = () => {
    this.setState({
      sportIds: [],
      selectedSports: [],
      kitNameError: '',
      saveError: ''
    });
  };

  private renderEditorView() {
    const {
      gender,
      shirtPattern,
      bottomPattern,
      primaryColor,
      secondaryColor,
      shortsPrimaryColor,
      shortsSecondaryColor,
      showPocket,
      showCollar,
      showSleeves,
      showShoulderTriangles,
      showSleeveCuffs,
      pocketColorSource,
      sleevesColorSource,
      collarColorSource,
      shoulderTrianglesColorSource,
      sleeveCuffsColorSource,
      pocketCustomColor,
      sleevesCustomColor,
      collarCustomColor,
      shoulderTrianglesCustomColor,
      sleeveCuffsCustomColor,
      collarStyle,
      stripeCount,
      stripesEqualWidth,
      stripePattern,
      skortStripeCount,
      skortStripesEqualWidth,
      skortStripePattern,
      diagonalDirection,
      chevronSize,
      chevronDirection,
      chevronPosition,
      sideTrianglesMode,
      sleevePattern,
      skortSideTrianglesMode,
      shortsTrianglesMode,
      editingPresetId,
      currentPresetName,
      editorStep,
      activeColorPicker,
      girlsBottomType,
      isSaving,
      sportIds,
      kitNameError,
      saveError
    } = this.state;

    const isGirls = gender === 'FEMALE';
    const visibleBottomPatterns = BOTTOM_PATTERNS.filter(pattern => {
      const isSkort = isSkortPatternId(pattern.id);
      if (gender === 'MALE') {
        return !isSkort;
      }
      if (girlsBottomType === 'SKORT') {
        return isSkort;
      }
      return !isSkort;
    });

    const effectiveSecondaryColor = secondaryColor;
    const effectiveShortsSecondaryColor = shortsSecondaryColor;

    const resolveDetailColor = (source: DetailColorSource, custom: string): string => {
      if (source === 'PRIMARY') return primaryColor;
      if (source === 'SECONDARY') return secondaryColor;
      return custom || secondaryColor || primaryColor;
    };

    const pocketColor = resolveDetailColor(pocketColorSource, pocketCustomColor);
    const sleevesColor = resolveDetailColor(sleevesColorSource, sleevesCustomColor);
    const collarColor = resolveDetailColor(collarColorSource, collarCustomColor);

    const shoulderTrianglesColor = resolveDetailColor(shoulderTrianglesColorSource, shoulderTrianglesCustomColor);
    const sleeveCuffsColor = resolveDetailColor(sleeveCuffsColorSource, sleeveCuffsCustomColor);

    const visibleShirtPatterns = SHIRT_PATTERNS;

    const isStripePattern =
      shirtPattern === 'horizontalStripes' || shirtPattern === 'verticalStripes' || shirtPattern === 'diagonal';

    const isSkortVerticalStripes = bottomPattern === 'SKORT_VERTICAL_STRIPES';
    const selectedSportsForDisplay = this.getSelectedSportsForDisplay();

    return (
      <div className="container-fluid eKitLab eKitLab--editor">
        {this.renderSportsModal()}
        {this.renderUnsavedChangesModal()}
        <header className="eKitLab__pageHeader eKitLab__editorHeader">
          <div className="eKitLab__editorHeading">
            <button type="button" className="eKitLab__backLink" onClick={this.handleBackToList} disabled={isSaving}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Kit lab
            </button>
            <span className="eKitLab__headerDivider" aria-hidden="true" />
            <div className="eKitLab__headerCopy">
              <h1 className="eKitLab__title">{editingPresetId ? 'Edit kit' : 'Create kit'}</h1>
              <p className="eKitLab__subtitle">
                {editingPresetId ? 'Update the kit details and design.' : 'Add the kit details, then build its design.'}
              </p>
            </div>
          </div>
          <div className="eKitLab__editorActions">
            <button
              type="button"
              className="eKitLab__button eKitLab__button--primary"
              onClick={this.handleSavePreset}
              disabled={isSaving}
            >
              {!isSaving && <FontAwesomeIcon icon={faCheck} />}
              {isSaving ? 'Saving…' : 'Save kit'}
            </button>
          </div>
        </header>

        <div className="eKitLab__detailsCard">
          <div className="row">
            <div className="col-md-8">
              <div className="form-row mt-2">
                <div className="col-md-7">
                  <div className="form-group mb-2">
                    <label htmlFor="kitLabPresetName">
                      Kit name <span className="eKitLab__requiredMark">*</span>
                    </label>
                    <input
                      id="kitLabPresetName"
                      type="text"
                      className={`form-control${kitNameError ? ' eKitLab__input--invalid' : ''}`}
                      value={currentPresetName}
                      placeholder={editingPresetId ? 'E.g. U13 girls away kit' : 'E.g. U11 boys home kit'}
                      onChange={this.onCurrentPresetNameChange}
                      onBlur={this.validateKitName}
                      aria-invalid={Boolean(kitNameError)}
                      aria-describedby={kitNameError ? 'kitLabPresetNameError' : undefined}
                      required
                    />
                    {kitNameError && (
                      <div id="kitLabPresetNameError" className="eKitLab__fieldError" role="alert">
                        {kitNameError}
                      </div>
                    )}
                  </div>
                  <div className="form-row mt-2">
                    <div className="col-md-12">
                      <div className="form-group mb-2">
                        <label className="d-block">Sports / activities for this kit (optional)</label>

                        <div className="d-flex align-items-center flex-wrap">
                          <button
                            type="button"
                            className="eKitLab__button eKitLab__button--secondary eKitLab__sportsButton"
                            onClick={this.openSportsModal}
                          >
                            {sportIds.length ? `Edit sports (${sportIds.length} selected)` : 'Select sports'}
                          </button>
                        </div>

                        {selectedSportsForDisplay.length > 0 && (
                          <div className="mt-2 d-flex flex-wrap eKitLab__sportsTags">
                            {selectedSportsForDisplay.map(sport => (
                              <span key={sport.id} className="eKitLab__selectedSportChip">
                                {sport.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-5">
                  <div className="form-row">
                    <div className={gender === 'FEMALE' ? 'col-md-6' : 'col-md-12'}>
                      <div className="form-group mb-2">
                        <span className="eKitLab__choiceLabel" id="kitLabGenderLabel">
                          Gender
                        </span>
                        <div className="eKitLab__choiceChips" role="group" aria-labelledby="kitLabGenderLabel">
                          <button
                            type="button"
                            className={`eKitLab__choiceChip${gender === 'MALE' ? ' eKitLab__choiceChip--active' : ''}`}
                            aria-pressed={gender === 'MALE'}
                            onClick={() => this.onGenderChange('MALE')}
                          >
                            Boys team
                          </button>
                          <button
                            type="button"
                            className={`eKitLab__choiceChip${
                              gender === 'FEMALE' ? ' eKitLab__choiceChip--active' : ''
                            }`}
                            aria-pressed={gender === 'FEMALE'}
                            onClick={() => this.onGenderChange('FEMALE')}
                          >
                            Girls team
                          </button>
                        </div>
                      </div>
                    </div>

                    {gender === 'FEMALE' && (
                      <div className="col-md-6">
                        <div className="form-group mb-2">
                          <span className="eKitLab__choiceLabel" id="kitLabGirlsBottomTypeLabel">
                            Girls bottom
                          </span>
                          <div
                            className="eKitLab__choiceChips"
                            role="group"
                            aria-labelledby="kitLabGirlsBottomTypeLabel"
                          >
                            <button
                              type="button"
                              className={`eKitLab__choiceChip${
                                girlsBottomType === 'SKORT' ? ' eKitLab__choiceChip--active' : ''
                              }`}
                              aria-pressed={girlsBottomType === 'SKORT'}
                              onClick={() => this.onGirlsBottomTypeChange('SKORT')}
                            >
                              Skort
                            </button>
                            <button
                              type="button"
                              className={`eKitLab__choiceChip${
                                girlsBottomType === 'SHORTS' ? ' eKitLab__choiceChip--active' : ''
                              }`}
                              aria-pressed={girlsBottomType === 'SHORTS'}
                              onClick={() => this.onGirlsBottomTypeChange('SHORTS')}
                            >
                              Shorts
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {saveError && (
          <div className="eKitLab__formError" role="alert">
            {saveError}
          </div>
        )}

        <div className="row mb-3">
          <div className="col-md-12">
            <div className="eKitLab__steps">
              <button
                type="button"
                className={'eKitLab__stepTab' + (editorStep === 1 ? ' eKitLab__stepTab--active' : '')}
                onClick={() => this.setEditorStep(1)}
              >
                <span className="eKitLab__stepTabNumber">1</span>
                <span className="eKitLab__stepTabLabel">Shirt base</span>
              </button>

              <button
                type="button"
                className={'eKitLab__stepTab' + (editorStep === 2 ? ' eKitLab__stepTab--active' : '')}
                onClick={() => this.setEditorStep(2)}
              >
                <span className="eKitLab__stepTabNumber">2</span>
                <span className="eKitLab__stepTabLabel">Sleeves</span>
              </button>

              <button
                type="button"
                className={'eKitLab__stepTab' + (editorStep === 3 ? ' eKitLab__stepTab--active' : '')}
                onClick={() => this.setEditorStep(3)}
              >
                <span className="eKitLab__stepTabNumber">3</span>
                <span className="eKitLab__stepTabLabel">Collar</span>
              </button>

              <button
                type="button"
                className={'eKitLab__stepTab' + (editorStep === 4 ? ' eKitLab__stepTab--active' : '')}
                onClick={() => this.setEditorStep(4)}
              >
                <span className="eKitLab__stepTabNumber">4</span>
                <span className="eKitLab__stepTabLabel">Other details</span>
              </button>

              <button
                type="button"
                className={'eKitLab__stepTab' + (editorStep === 5 ? ' eKitLab__stepTab--active' : '')}
                onClick={() => this.setEditorStep(5)}
              >
                <span className="eKitLab__stepTabNumber">5</span>
                <span className="eKitLab__stepTabLabel">Shorts / skort</span>
              </button>
            </div>
          </div>
        </div>

        <div className="row eKitLab__designer">
          <div className="col-lg-4 d-flex flex-column align-items-center mb-4 mb-lg-0">
            <div className="eKitLab__previewCard">
              <div className="eKitLab__kitPreviewComposite">
                <KitShirtPreview
                  variant={shirtPattern}
                  primaryColor={primaryColor}
                  secondaryColor={effectiveSecondaryColor}
                  showPocket={showPocket}
                  showCollar={showCollar}
                  showSleeves={showSleeves}
                  showShoulderTriangles={showShoulderTriangles}
                  showSleeveCuffs={showSleeveCuffs}
                  pocketColor={pocketColor}
                  sleevesColor={sleevesColor}
                  collarColor={collarColor}
                  shoulderTrianglesColor={shoulderTrianglesColor}
                  sleeveCuffsColor={sleeveCuffsColor}
                  collarStyle={collarStyle}
                  stripeCount={stripeCount}
                  stripesEqualWidth={stripesEqualWidth}
                  stripePattern={stripePattern}
                  diagonalDirection={diagonalDirection}
                  chevronSize={chevronSize}
                  chevronDirection={chevronDirection}
                  chevronPosition={chevronPosition}
                  sideTrianglesMode={sideTrianglesMode}
                  sleevePattern={sleevePattern}
                  className="eKitLab__shirtSvg--big"
                />
                {isSkortPatternId(bottomPattern) ? (
                  <KitSkortPreview
                    pattern={mapBottomToSkortPattern(bottomPattern)}
                    primaryColor={shortsPrimaryColor}
                    secondaryColor={effectiveShortsSecondaryColor}
                    stripeCount={skortStripeCount}
                    stripesEqualWidth={skortStripesEqualWidth}
                    stripePattern={skortStripePattern}
                    sideTrianglesMode={skortSideTrianglesMode}
                    className="eKitLab__bottomSvg--big eKitLab__bottomSvg--bigSkort"
                  />
                ) : (
                  <KitShortsPreview
                    variant={
                      bottomPattern === 'SHORTS_SIDE'
                        ? 'sideStripes'
                        : bottomPattern === 'SHORTS_TRIANGLES'
                        ? 'sideTriangles'
                        : bottomPattern === 'SHORTS_BIG_TRIANGLES'
                        ? 'sideBigTriangles'
                        : bottomPattern === 'SHORTS_SEMICIRCLES'
                        ? 'sideSemiCircles'
                        : 'plain'
                    }
                    primaryColor={shortsPrimaryColor}
                    secondaryColor={effectiveShortsSecondaryColor}
                    sideTrianglesPosition={shortsTrianglesMode}
                    className="eKitLab__bottomSvg--big"
                  />
                )}
              </div>
            </div>
            <div className="eKitLab__previewCaption mt-2">
              {isGirls ? 'Girls kit preview (shirt + skort)' : 'Boys kit preview (shirt + shorts)'}
            </div>
          </div>

          <div className="col-lg-8">
            {editorStep === 1 && (
              <>
                <div className="mb-4">
                  <div className="eKitLab__stepTitle">Step 1 · Shirt base - Pick shirt pattern & colours</div>

                  <div className="eKitLab__patternGrid">
                    {visibleShirtPatterns.map(pattern => {
                      const isActive = pattern.id === shirtPattern;
                      const itemClass = 'eKitLab__patternTile' + (isActive ? ' eKitLab__patternTile--active' : '');

                      return (
                        <button
                          key={pattern.id}
                          type="button"
                          className={itemClass}
                          onClick={() => this.onShirtPatternClick(pattern.id)}
                        >
                          <KitShirtPreview
                            variant={pattern.id}
                            primaryColor={primaryColor}
                            secondaryColor={effectiveSecondaryColor}
                            showPocket={showPocket}
                            showCollar={showCollar}
                            showSleeves={showSleeves}
                            pocketColor={pocketColor}
                            sleevesColor={sleevesColor}
                            collarColor={collarColor}
                            shoulderTrianglesColor={shoulderTrianglesColor}
                            sleeveCuffsColor={sleeveCuffsColor}
                            stripeCount={stripeCount}
                            stripesEqualWidth={stripesEqualWidth}
                            stripePattern={stripePattern}
                            diagonalDirection={diagonalDirection}
                            chevronSize={chevronSize}
                            chevronDirection={chevronDirection}
                            chevronPosition={chevronPosition}
                            sideTrianglesMode={sideTrianglesMode}
                            sleevePattern={sleevePattern}
                            className="eKitLab__shirtSvg--small"
                          />
                          <div className="eKitLab__patternLabel">{pattern.label}</div>
                        </button>
                      );
                    })}
                  </div>

                  {isStripePattern && (
                    <div className="eKitLab__subOptions mt-3">
                      <div className="form-group mb-2 eKitLab__stripeCountRow">
                        <label htmlFor="kitLabStripeCount">Number of stripes (2 - 12)</label>
                        <div className="d-flex align-items-center mb-2">
                          <input
                            id="kitLabStripeCount"
                            type="number"
                            min={2}
                            max={12}
                            className="form-control form-control-sm eKitLab__stripeCountInput"
                            value={stripeCount}
                            onChange={this.onShirtStripeCountChange}
                          />
                          <div className="form-check mb-0 ml-3">
                            <input
                              id="kitLabEqualStripes"
                              type="checkbox"
                              className="form-check-input"
                              checked={stripesEqualWidth}
                              onChange={this.onShirtStripesEqualWidthChange}
                            />
                            <label htmlFor="kitLabEqualStripes" className="form-check-label">
                              All stripes the same width
                            </label>
                          </div>
                        </div>

                        {shirtPattern === 'diagonal' && (
                          <div className="d-flex align-items-center eKitLab__diagonalRow">
                            <span className="eKitLab__chipsLabel mb-0 mr-2">Diagonal direction</span>
                            <div className="eKitLab__toggleGroup" role="group">
                              <button
                                type="button"
                                className={'eKitLab__toggleBtn' + (diagonalDirection === 'RIGHT' ? ' active' : '')}
                                onClick={() => this.onDiagonalDirectionChange('RIGHT')}
                              >
                                <span className="eKitLab__diagonalIcon eKitLab__diagonalIcon--right" aria-hidden="true">
                                  &#x2199;
                                </span>
                                RIGHT
                              </button>

                              <button
                                type="button"
                                className={'eKitLab__toggleBtn' + (diagonalDirection === 'LEFT' ? ' active' : '')}
                                onClick={() => this.onDiagonalDirectionChange('LEFT')}
                              >
                                <span className="eKitLab__diagonalIcon" aria-hidden="true">
                                  &#x2199;
                                </span>
                                LEFT
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {!stripesEqualWidth && (
                        <div className="mt-2">
                          <div className="eKitLab__chipsLabel">
                            Stripe widths (Click a stripe to toggle between narrow and wide.)
                          </div>
                          <div className="eKitLab__chipsRow">
                            {Array.from({ length: stripeCount }, (_, i) => {
                              const ratio = stripePattern[i] || 1;
                              const isWide = ratio > 1;
                              const chipClass = 'eKitLab__stripeChip' + (isWide ? ' eKitLab__stripeChip--wide' : '');

                              return (
                                <button
                                  key={i}
                                  type="button"
                                  className={chipClass}
                                  onClick={() => this.onShirtStripeChipToggle(i)}
                                >
                                  {i + 1}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {shirtPattern === 'chevron' && (
                    <div className="eKitLab__subOptions mt-3">
                      <div className="eKitLab__chevronRow">
                        <div className="eKitLab__chevronField">
                          <div className="eKitLab__chipsLabel mb-1">Chevron size</div>
                          <div className="eKitLab__toggleGroup" role="group">
                            <button
                              type="button"
                              className={'eKitLab__toggleBtn' + (chevronSize === 'SMALL' ? ' active' : '')}
                              onClick={() => this.onChevronSizeChange('SMALL')}
                            >
                              Small
                            </button>
                            <button
                              type="button"
                              className={'eKitLab__toggleBtn' + (chevronSize === 'BIG' ? ' active' : '')}
                              onClick={() => this.onChevronSizeChange('BIG')}
                            >
                              Big
                            </button>
                          </div>
                        </div>

                        <div className="eKitLab__chevronField">
                          <div className="eKitLab__chipsLabel mb-1">Chevron direction</div>
                          <div className="eKitLab__toggleGroup" role="group">
                            <button
                              type="button"
                              className={'eKitLab__toggleBtn' + (chevronDirection === 'DOWN' ? ' active' : '')}
                              onClick={() => this.onChevronDirectionChange('DOWN')}
                            >
                              ↓ Down
                            </button>
                            <button
                              type="button"
                              className={'eKitLab__toggleBtn' + (chevronDirection === 'UP' ? ' active' : '')}
                              onClick={() => this.onChevronDirectionChange('UP')}
                            >
                              ↑ Up
                            </button>
                          </div>
                        </div>

                        <div className="eKitLab__chevronField">
                          <div className="eKitLab__chipsLabel mb-1">Chevron position</div>
                          <div className="eKitLab__toggleGroup" role="group">
                            <button
                              type="button"
                              className={'eKitLab__toggleBtn' + (chevronPosition === 'TOP' ? ' active' : '')}
                              onClick={() => this.onChevronPositionChange('TOP')}
                            >
                              Top
                            </button>
                            <button
                              type="button"
                              className={'eKitLab__toggleBtn' + (chevronPosition === 'MIDDLE' ? ' active' : '')}
                              onClick={() => this.onChevronPositionChange('MIDDLE')}
                            >
                              Middle
                            </button>
                            <button
                              type="button"
                              className={'eKitLab__toggleBtn' + (chevronPosition === 'BOTTOM' ? ' active' : '')}
                              onClick={() => this.onChevronPositionChange('BOTTOM')}
                            >
                              Bottom
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {shirtPattern === 'sideTriangles' && (
                    <div className="eKitLab__subOptions mt-3">
                      <label className="d-block eKitLab__chipsLabel">Side triangles position</label>
                      <div className="eKitLab__toggleGroup" role="group">
                        <button
                          type="button"
                          className={'eKitLab__toggleBtn' + (sideTrianglesMode === 'TOP' ? ' active' : '')}
                          onClick={() => this.onSideTrianglesModeChange('TOP')}
                        >
                          Top only
                        </button>
                        <button
                          type="button"
                          className={'eKitLab__toggleBtn' + (sideTrianglesMode === 'BOTTOM' ? ' active' : '')}
                          onClick={() => this.onSideTrianglesModeChange('BOTTOM')}
                        >
                          Bottom only
                        </button>
                        <button
                          type="button"
                          className={'eKitLab__toggleBtn' + (sideTrianglesMode === 'BOTH' ? ' active' : '')}
                          onClick={() => this.onSideTrianglesModeChange('BOTH')}
                        >
                          Top &amp; bottom
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="row eKitLab__optionsRow eKitLab__optionsRow--details">
                  <div className="col-md-3 mb-3">
                    <div className="form-group eKitLab__colorField">
                      <label htmlFor="kitLabPrimaryColor">Primary colour</label>
                      <button
                        id="kitLabPrimaryColor"
                        type="button"
                        className="eKitLab__colorBar"
                        style={{ backgroundColor: primaryColor }}
                        onClick={() => this.openColorPicker('PRIMARY')}
                      />
                      {activeColorPicker === 'PRIMARY' && (
                        <div className="eKitLab__colorPopover">
                          <SketchPicker color={primaryColor} onChange={c => this.handleSketchChange('PRIMARY', c)} />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-3 mb-3">
                    <div className="form-group eKitLab__colorField">
                      <label htmlFor="kitLabSecondaryColor">Secondary colour</label>
                      <button
                        id="kitLabSecondaryColor"
                        type="button"
                        className="eKitLab__colorBar"
                        style={{ backgroundColor: secondaryColor }}
                        onClick={() => this.openColorPicker('SECONDARY')}
                      />
                      {activeColorPicker === 'SECONDARY' && (
                        <div className="eKitLab__colorPopover">
                          <SketchPicker
                            color={secondaryColor}
                            onChange={c => this.handleSketchChange('SECONDARY', c)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {editorStep === 2 && (
              <>
                <div className="mb-4">
                  <div className="eKitLab__stepTitle">Step 2 · Sleeves</div>
                </div>

                <div className="row eKitLab__optionsRow eKitLab__optionsRow--details">
                  <div className="col-md-6 mb-3">
                    <div className="eKitLab__detailCard">
                      <div className="eKitLab__detailHeader">
                        <div className="form-check mb-0">
                          <input
                            id="kitLabSleeves"
                            type="checkbox"
                            className="form-check-input"
                            checked={showSleeves}
                            onChange={this.onToggleSleeves}
                          />
                          <label htmlFor="kitLabSleeves" className="form-check-label eKitLab__detailTitle">
                            Sleeves
                          </label>
                        </div>
                      </div>

                      {showSleeves && (
                        <>
                          <div className="mb-2">
                            <div className="eKitLab__toggleGroup" role="group">
                              <button
                                type="button"
                                className={'eKitLab__toggleBtn' + (sleevePattern === 'PLAIN' ? ' active' : '')}
                                onClick={() => this.setState({ sleevePattern: 'PLAIN' })}
                              >
                                Plain
                              </button>
                              <button
                                type="button"
                                className={'eKitLab__toggleBtn' + (sleevePattern === 'TOP_STRIPE' ? ' active' : '')}
                                onClick={() => this.setState({ sleevePattern: 'TOP_STRIPE' })}
                              >
                                Stripe at top
                              </button>
                              <button
                                type="button"
                                className={'eKitLab__toggleBtn' + (sleevePattern === 'TOP_TRIANGLES' ? ' active' : '')}
                                onClick={() => this.setState({ sleevePattern: 'TOP_TRIANGLES' })}
                              >
                                Top triangles
                              </button>
                            </div>
                          </div>

                          <div className="btn-group btn-group-sm mb-2 eKitLab__detailSourceBtns" role="group">
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' + (sleevesColorSource === 'PRIMARY' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('SLEEVES', 'PRIMARY')}
                            >
                              Primary
                            </button>
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' + (sleevesColorSource === 'SECONDARY' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('SLEEVES', 'SECONDARY')}
                            >
                              Secondary
                            </button>
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' + (sleevesColorSource === 'CUSTOM' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('SLEEVES', 'CUSTOM')}
                            >
                              Custom
                            </button>
                          </div>

                          {sleevesColorSource === 'CUSTOM' ? (
                            <div className="eKitLab__colorField">
                              <button
                                type="button"
                                className="eKitLab__colorBar"
                                style={{ backgroundColor: sleevesCustomColor || secondaryColor }}
                                onClick={() => this.openColorPicker('SLEEVES_CUSTOM')}
                              />
                              {activeColorPicker === 'SLEEVES_CUSTOM' && (
                                <div className="eKitLab__colorPopover">
                                  <SketchPicker
                                    color={sleevesCustomColor || secondaryColor}
                                    onChange={c => this.handleSketchChange('SLEEVES_CUSTOM', c)}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="eKitLab__detailHint">
                              Uses {sleevesColorSource === 'PRIMARY' ? 'primary' : 'secondary'} shirt colour.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="eKitLab__detailCard">
                      <div className="eKitLab__detailHeader">
                        <div className="form-check mb-0">
                          <input
                            id="kitLabSleeveCuffs"
                            type="checkbox"
                            className="form-check-input"
                            checked={showSleeveCuffs}
                            onChange={this.onToggleSleeveCuffs}
                          />
                          <label htmlFor="kitLabSleeveCuffs" className="form-check-label eKitLab__detailTitle">
                            Sleeve cuffs (trim)
                          </label>
                        </div>
                      </div>

                      {showSleeveCuffs && (
                        <>
                          <div className="btn-group btn-group-sm mb-2 eKitLab__detailSourceBtns" role="group">
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' + (sleeveCuffsColorSource === 'PRIMARY' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('SLEEVE_CUFFS', 'PRIMARY')}
                            >
                              Primary
                            </button>
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' + (sleeveCuffsColorSource === 'SECONDARY' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('SLEEVE_CUFFS', 'SECONDARY')}
                            >
                              Secondary
                            </button>
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' + (sleeveCuffsColorSource === 'CUSTOM' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('SLEEVE_CUFFS', 'CUSTOM')}
                            >
                              Custom
                            </button>
                          </div>

                          {sleeveCuffsColorSource === 'CUSTOM' ? (
                            <div className="eKitLab__colorField">
                              <button
                                type="button"
                                className="eKitLab__colorBar"
                                style={{ backgroundColor: sleeveCuffsCustomColor || secondaryColor }}
                                onClick={() => this.openColorPicker('SLEEVE_CUFFS_CUSTOM')}
                              />
                              {activeColorPicker === 'SLEEVE_CUFFS_CUSTOM' && (
                                <div className="eKitLab__colorPopover">
                                  <SketchPicker
                                    color={sleeveCuffsCustomColor || secondaryColor}
                                    onChange={c => this.handleSketchChange('SLEEVE_CUFFS_CUSTOM', c)}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="eKitLab__detailHint">
                              Uses {sleeveCuffsColorSource === 'PRIMARY' ? 'primary' : 'secondary'} shirt colour.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {editorStep === 3 && (
              <>
                <div className="mb-4">
                  <div className="eKitLab__stepTitle">Step 3 · Collar</div>
                  <div className="eKitLab__hint">Choose collar / neckline style and colour.</div>
                </div>

                <div className="row eKitLab__optionsRow eKitLab__optionsRow--details">
                  <div className="col-md-6 col-lg-7 mb-3">
                    <div className="eKitLab__detailCard">
                      <div className="eKitLab__detailHeader">
                        <div className="form-check mb-0">
                          <input
                            id="kitLabCollar"
                            type="checkbox"
                            className="form-check-input"
                            checked={showCollar}
                            onChange={this.onToggleCollar}
                          />
                          <label htmlFor="kitLabCollar" className="form-check-label eKitLab__detailTitle">
                            Collar / neckline
                          </label>
                        </div>
                      </div>

                      {showCollar && (
                        <>
                          <div className="btn-group btn-group-sm mb-2 eKitLab__detailSourceBtns" role="group">
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' + (collarColorSource === 'PRIMARY' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('COLLAR', 'PRIMARY')}
                            >
                              Primary
                            </button>
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' + (collarColorSource === 'SECONDARY' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('COLLAR', 'SECONDARY')}
                            >
                              Secondary
                            </button>
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' + (collarColorSource === 'CUSTOM' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('COLLAR', 'CUSTOM')}
                            >
                              Custom
                            </button>
                          </div>

                          {collarColorSource === 'CUSTOM' ? (
                            <div className="eKitLab__colorField">
                              <button
                                type="button"
                                className="eKitLab__colorBar"
                                style={{ backgroundColor: collarCustomColor || secondaryColor }}
                                onClick={() => this.openColorPicker('COLLAR_CUSTOM')}
                              />
                              {activeColorPicker === 'COLLAR_CUSTOM' && (
                                <div className="eKitLab__colorPopover">
                                  <SketchPicker
                                    color={collarCustomColor || secondaryColor}
                                    onChange={c => this.handleSketchChange('COLLAR_CUSTOM', c)}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="eKitLab__detailHint">
                              Uses {collarColorSource === 'PRIMARY' ? 'primary' : 'secondary'} shirt colour.
                            </div>
                          )}
                          <div className="mt-3">
                            <label className="d-block eKitLab__chipsLabel">Collar style</label>
                            <div className="eKitLab__toggleGroup" role="group">
                              <button
                                type="button"
                                className={'eKitLab__toggleBtn' + (collarStyle === 'ROUND' ? ' active' : '')}
                                onClick={() => this.setState({ collarStyle: 'ROUND' })}
                              >
                                Round / crew neck
                              </button>
                              <button
                                type="button"
                                className={'eKitLab__toggleBtn' + (collarStyle === 'POLO' ? ' active' : '')}
                                onClick={() => this.setState({ collarStyle: 'POLO' })}
                              >
                                V-neck / polo
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {editorStep === 4 && (
              <>
                <div className="mb-4">
                  <div className="eKitLab__stepTitle">Step 4 · Other details</div>
                  <div className="eKitLab__hint">
                    Adjust chest pocket and shoulder epaulettes – turn them on/off and choose their colours.
                  </div>
                </div>

                <div className="row eKitLab__optionsRow eKitLab__optionsRow--details">
                  <div className="col-md-4 mb-3">
                    <div className="eKitLab__detailCard">
                      <div className="eKitLab__detailHeader">
                        <div className="form-check mb-0">
                          <input
                            id="kitLabPocket"
                            type="checkbox"
                            className="form-check-input"
                            checked={showPocket}
                            onChange={this.onTogglePocket}
                          />
                          <label htmlFor="kitLabPocket" className="form-check-label eKitLab__detailTitle">
                            Chest pocket
                          </label>
                        </div>
                      </div>

                      {showPocket && (
                        <>
                          <div className="btn-group btn-group-sm mb-2 eKitLab__detailSourceBtns" role="group">
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' + (pocketColorSource === 'PRIMARY' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('POCKET', 'PRIMARY')}
                            >
                              Primary
                            </button>
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' + (pocketColorSource === 'SECONDARY' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('POCKET', 'SECONDARY')}
                            >
                              Secondary
                            </button>
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' + (pocketColorSource === 'CUSTOM' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('POCKET', 'CUSTOM')}
                            >
                              Custom
                            </button>
                          </div>
                          {pocketColorSource === 'CUSTOM' ? (
                            <div className="eKitLab__colorField">
                              <button
                                type="button"
                                className="eKitLab__colorBar"
                                style={{ backgroundColor: pocketCustomColor || secondaryColor }}
                                onClick={() => this.openColorPicker('POCKET_CUSTOM')}
                              />
                              {activeColorPicker === 'POCKET_CUSTOM' && (
                                <div className="eKitLab__colorPopover">
                                  <SketchPicker
                                    color={pocketCustomColor || secondaryColor}
                                    onChange={c => this.handleSketchChange('POCKET_CUSTOM', c)}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="eKitLab__detailHint">
                              Uses {pocketColorSource === 'PRIMARY' ? 'primary' : 'secondary'} shirt colour.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="col-md-4 mb-3">
                    <div className="eKitLab__detailCard">
                      <div className="eKitLab__detailHeader">
                        <div className="form-check mb-0">
                          <input
                            id="kitLabShoulderTriangles"
                            type="checkbox"
                            className="form-check-input"
                            checked={showShoulderTriangles}
                            onChange={this.onToggleShoulderTriangles}
                          />
                          <label htmlFor="kitLabShoulderTriangles" className="form-check-label eKitLab__detailTitle">
                            Shoulder epaulettes
                          </label>
                        </div>
                      </div>

                      {showShoulderTriangles && (
                        <>
                          <div className="btn-group btn-group-sm mb-2 eKitLab__detailSourceBtns" role="group">
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' +
                                (shoulderTrianglesColorSource === 'PRIMARY' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('SHOULDER_TRIANGLES', 'PRIMARY')}
                            >
                              Primary
                            </button>
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' +
                                (shoulderTrianglesColorSource === 'SECONDARY' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('SHOULDER_TRIANGLES', 'SECONDARY')}
                            >
                              Secondary
                            </button>
                            <button
                              type="button"
                              className={
                                'btn btn-outline-secondary' +
                                (shoulderTrianglesColorSource === 'CUSTOM' ? ' active' : '')
                              }
                              onClick={() => this.onDetailColorSourceChange('SHOULDER_TRIANGLES', 'CUSTOM')}
                            >
                              Custom
                            </button>
                          </div>

                          {shoulderTrianglesColorSource === 'CUSTOM' ? (
                            <div className="eKitLab__colorField mb-2">
                              <button
                                type="button"
                                className="eKitLab__colorBar"
                                style={{
                                  backgroundColor: shoulderTrianglesCustomColor || secondaryColor
                                }}
                                onClick={() => this.openColorPicker('SHOULDER_TRIANGLES_CUSTOM')}
                              />
                              {activeColorPicker === 'SHOULDER_TRIANGLES_CUSTOM' && (
                                <div className="eKitLab__colorPopover">
                                  <SketchPicker
                                    color={shoulderTrianglesCustomColor || secondaryColor}
                                    onChange={c => this.handleSketchChange('SHOULDER_TRIANGLES_CUSTOM', c)}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="eKitLab__detailHint mb-2">
                              Uses {shoulderTrianglesColorSource === 'PRIMARY' ? 'primary' : 'secondary'} shirt colour.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {editorStep === 5 && (
              <>
                <div className="mb-4">
                  <div className="eKitLab__stepTitle">Step 5 · Shorts / skort</div>
                  <div className="eKitLab__hint">Choose the lower part and adjust its colours.</div>
                  <div className="eKitLab__patternGrid">
                    {visibleBottomPatterns.map(pattern => {
                      const isSkortPattern = isSkortPatternId(pattern.id);
                      const isActive = pattern.id === bottomPattern;
                      const itemClass = 'eKitLab__patternTile' + (isActive ? ' eKitLab__patternTile--active' : '');

                      return (
                        <button
                          key={pattern.id}
                          type="button"
                          className={itemClass}
                          onClick={() => this.onBottomPatternClick(pattern.id)}
                        >
                          {isSkortPattern ? (
                            <KitSkortPreview
                              pattern={mapBottomToSkortPattern(pattern.id)}
                              primaryColor={shortsPrimaryColor}
                              secondaryColor={effectiveShortsSecondaryColor}
                              stripeCount={skortStripeCount}
                              stripesEqualWidth={skortStripesEqualWidth}
                              stripePattern={skortStripePattern}
                              sideTrianglesMode={skortSideTrianglesMode}
                              className="eKitLab__bottomSvg--small eKitLab__bottomSvg--smallSkort"
                            />
                          ) : (
                            <KitShortsPreview
                              variant={
                                pattern.id === 'SHORTS_SIDE'
                                  ? 'sideStripes'
                                  : pattern.id === 'SHORTS_TRIANGLES'
                                  ? 'sideTriangles'
                                  : pattern.id === 'SHORTS_BIG_TRIANGLES'
                                  ? 'sideBigTriangles'
                                  : pattern.id === 'SHORTS_SEMICIRCLES'
                                  ? 'sideSemiCircles'
                                  : 'plain'
                              }
                              primaryColor={shortsPrimaryColor}
                              secondaryColor={effectiveShortsSecondaryColor}
                              sideTrianglesPosition={shortsTrianglesMode}
                              className="eKitLab__bottomSvg--small"
                            />
                          )}
                          <div className="eKitLab__patternLabel">{pattern.label}</div>
                        </button>
                      );
                    })}
                  </div>
                  {isSkortVerticalStripes && (
                    <div className="eKitLab__subOptions mt-3">
                      <div className="form-group mb-2 eKitLab__stripeCountRow">
                        <label htmlFor="kitLabSkortStripeCount">Number of stripes (2 - 12)</label>
                        <div className="d-flex align-items-center">
                          <input
                            id="kitLabSkortStripeCount"
                            type="number"
                            min={2}
                            max={12}
                            className="form-control form-control-sm eKitLab__stripeCountInput"
                            value={skortStripeCount}
                            onChange={this.onSkortStripeCountChange}
                          />
                          <div className="form-check mb-0 ml-3">
                            <input
                              id="kitLabSkortEqualStripes"
                              type="checkbox"
                              className="form-check-input"
                              checked={skortStripesEqualWidth}
                              onChange={this.onSkortStripesEqualWidthChange}
                            />
                            <label htmlFor="kitLabSkortEqualStripes" className="form-check-label">
                              All skort stripes the same width
                            </label>
                          </div>
                        </div>
                      </div>
                      {!skortStripesEqualWidth && (
                        <div className="mt-2">
                          <div className="eKitLab__chipsLabel">Skort stripe widths</div>
                          <div className="eKitLab__chipsRow">
                            {Array.from({ length: skortStripeCount }, (_, i) => {
                              const ratio = skortStripePattern[i] || 1;
                              const isWide = ratio > 1;
                              const chipClass = 'eKitLab__stripeChip' + (isWide ? ' eKitLab__stripeChip--wide' : '');

                              return (
                                <button
                                  key={i}
                                  type="button"
                                  className={chipClass}
                                  onClick={() => this.onSkortStripeChipToggle(i)}
                                >
                                  {i + 1}
                                </button>
                              );
                            })}
                          </div>
                          <small className="form-text text-muted">
                            Click a stripe to toggle between narrow and wide.
                          </small>
                        </div>
                      )}
                    </div>
                  )}

                  {bottomPattern === 'SHORTS_TRIANGLES' && (
                    <div className="eKitLab__subOptions mt-3">
                      <label className="d-block">Side triangles position</label>
                      <div className="eKitLab__toggleGroup" role="group">
                        <button
                          type="button"
                          className={'eKitLab__toggleBtn' + (shortsTrianglesMode === 'TOP' ? ' active' : '')}
                          onClick={() => this.onShortsTrianglesModeChange('TOP')}
                        >
                          Top
                        </button>
                        <button
                          type="button"
                          className={'eKitLab__toggleBtn' + (shortsTrianglesMode === 'BOTH' ? ' active' : '')}
                          onClick={() => this.onShortsTrianglesModeChange('BOTH')}
                        >
                          Top &amp; bottom
                        </button>
                        <button
                          type="button"
                          className={'eKitLab__toggleBtn' + (shortsTrianglesMode === 'BOTTOM' ? ' active' : '')}
                          onClick={() => this.onShortsTrianglesModeChange('BOTTOM')}
                        >
                          Bottom
                        </button>
                      </div>
                    </div>
                  )}

                  {bottomPattern === 'SKORT_TRIANGLES' && (
                    <div className="eKitLab__subOptions mt-3">
                      <label className="d-block">Skort side triangles position</label>
                      <div className="eKitLab__toggleGroup" role="group">
                        <button
                          type="button"
                          className={'eKitLab__toggleBtn' + (skortSideTrianglesMode === 'TOP' ? ' active' : '')}
                          onClick={() => this.onSkortSideTrianglesModeChange('TOP')}
                        >
                          Top only
                        </button>
                        <button
                          type="button"
                          className={'eKitLab__toggleBtn' + (skortSideTrianglesMode === 'BOTTOM' ? ' active' : '')}
                          onClick={() => this.onSkortSideTrianglesModeChange('BOTTOM')}
                        >
                          Bottom only
                        </button>
                        <button
                          type="button"
                          className={'eKitLab__toggleBtn' + (skortSideTrianglesMode === 'BOTH' ? ' active' : '')}
                          onClick={() => this.onSkortSideTrianglesModeChange('BOTH')}
                        >
                          Top &amp; bottom
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="row eKitLab__optionsRow eKitLab__optionsRow--details">
                  <div className="col-md-3 mb-3">
                    <div className="form-group eKitLab__colorField">
                      <label htmlFor="kitLabShortsPrimaryColor">Primary colour</label>
                      <button
                        id="kitLabShortsPrimaryColor"
                        type="button"
                        className="eKitLab__colorBar"
                        style={{ backgroundColor: shortsPrimaryColor }}
                        onClick={() => this.openColorPicker('SHORTS_PRIMARY')}
                      />
                      <small className="form-text text-muted">Main shorts / skort colour.</small>

                      {activeColorPicker === 'SHORTS_PRIMARY' && (
                        <div className="eKitLab__colorPopover">
                          <SketchPicker
                            color={shortsPrimaryColor}
                            onChange={c => this.handleSketchChange('SHORTS_PRIMARY', c)}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-3 mb-3">
                    <div className="form-group eKitLab__colorField">
                      <label htmlFor="kitLabShortsSecondaryColor">Secondary colour</label>
                      <button
                        id="kitLabShortsSecondaryColor"
                        type="button"
                        className="eKitLab__colorBar"
                        style={{ backgroundColor: shortsSecondaryColor }}
                        onClick={() => this.openColorPicker('SHORTS_SECONDARY')}
                      />
                      <small className="form-text text-muted">Stripes / triangles on shorts.</small>

                      {activeColorPicker === 'SHORTS_SECONDARY' && (
                        <div className="eKitLab__colorPopover">
                          <SketchPicker
                            color={shortsSecondaryColor}
                            onChange={c => this.handleSketchChange('SHORTS_SECONDARY', c)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeColorPicker && <div className="eKitLab__colorOverlay" onClick={this.closeColorPicker} />}
          </div>
        </div>
      </div>
    );
  }

  private renderSportsModal() {
    const { isSportsModalOpen, selectedSports, sportIds } = this.state;

    if (!isSportsModalOpen) return null;

    const selectedSportsForDisplay = this.getSelectedSportsForDisplay();

    return (
      <SimpleModal
        isOpen={true}
        title="Limit kit to sports / activities"
        customClass="eKitLab__sportsModal"
        dialogStyle={{ maxWidth: 480 }}
        onCloseClick={this.closeSportsModal}
        showFooter={false}
      >
        <p className="eKitLab__sportsModalHelp">
          Choose where this kit is available. Leave empty to allow it for all sports and activities.
        </p>

        <div className="eKitLab__sportsModalSearch">
          <label className="eKitLab__sportsModalLabel">Search sports / activities</label>

          <Autocomplete
            searchFunction={this.getSportsForKit}
            getElementTitle={getName}
            customClass="mFullWidth"
            defaultItem={undefined}
            placeholder="Start typing the sport / activity name"
            onSelect={this.handleSportSelected}
          />
        </div>

        <div className="eKitLab__sportsModalSelected">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="eKitLab__chipsLabel mb-0">
              Selected sports {sportIds.length ? `(${sportIds.length})` : ''}
            </span>

            {selectedSportsForDisplay.length > 0 && (
              <button
                type="button"
                className="eKitLab__textButton eKitLab__textButton--danger"
                onClick={this.clearSports}
              >
                Clear all
              </button>
            )}
          </div>

          {selectedSportsForDisplay.length === 0 ? (
            <div className="eKitLab__emptySelected">
              <span className="text-muted">No sports selected – this kit will be available for all sports.</span>
            </div>
          ) : (
            <div className="eKitLab__sportsTags">
              {selectedSportsForDisplay.map(sport => (
                <div key={sport.id} className="eKitLab__sportTag">
                  <span className="eKitLab__sportTagName">{sport.name}</span>
                  <button
                    type="button"
                    className="eKitLab__sportTagRemove"
                    onClick={() => this.handleRemoveSport(sport.id)}
                    aria-label={`Remove ${sport.name}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="eKitLab__sportsModalFooter">
          <button type="button" className="eKitLab__button eKitLab__button--primary" onClick={this.closeSportsModal}>
            Done
          </button>
        </div>
      </SimpleModal>
    );
  }

  private renderUnsavedChangesModal() {
    const { isUnsavedModalOpen } = this.state;

    if (!isUnsavedModalOpen) return null;

    return (
      <SimpleModal
        isOpen={true}
        title="Discard unsaved changes?"
        customClass="eKitLab__unsavedModal"
        onCloseClick={this.handleCancelDiscardChanges}
        showFooter={false}
      >
        <div className="eKitLab__unsavedModalBody">
          <div className="eKitLab__unsavedModalIcon">!</div>
          <div className="eKitLab__unsavedModalText">
            <h5>Unsaved changes</h5>
            <p>
              You&apos;ve made changes to this kit that haven&apos;t been saved yet. If you go back now, these changes
              will be lost.
            </p>
          </div>
        </div>

        <div className="eKitLab__unsavedModalActions">
          <button
            type="button"
            className="eKitLab__button eKitLab__button--secondary"
            onClick={this.handleCancelDiscardChanges}
          >
            Continue editing
          </button>

          <button
            type="button"
            className="eKitLab__button eKitLab__button--danger"
            onClick={this.handleConfirmDiscardChanges}
          >
            Discard changes &amp; go back
          </button>
        </div>
      </SimpleModal>
    );
  }

  private haveSameSports(a: KitPreset, b: KitPreset): boolean {
    const norm = (arr?: string[]) => (arr || []).map(id => id.toString()).sort();

    const aSports = norm(a.sportIds);
    const bSports = norm(b.sportIds);

    if (aSports.length !== bSports.length) return false;
    return aSports.every((id, idx) => id === bSports[idx]);
  }

  private getSelectedSportsForDisplay(): { id: string; name: string }[] {
    const { sportIds, selectedSports, sportsMap } = this.state;

    if (!sportIds || sportIds.length === 0) {
      return [];
    }

    const fromSelected: { [id: string]: string } = {};
    (selectedSports || []).forEach(s => {
      if (!s || !s.id) return;
      fromSelected[s.id.toString()] = s.name;
    });

    return sportIds.map(rawId => {
      const key = rawId.toString();
      const nameFromSelected = fromSelected[key];
      const nameFromMap = sportsMap[key];
      const name = nameFromSelected || nameFromMap || 'Unknown sport';
      return { id: key, name };
    });
  }

  render() {
    const { viewMode } = this.state;

    if (viewMode === 'LIST') {
      return this.renderListView();
    }

    return this.renderEditorView();
  }
}
